import { useState } from "react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import { Pencil, Trash2, Plus, History } from "lucide-react";
import { AttendanceDaySummary, AttendancePunch, fetchAttendanceDayDetail, toAttendanceRecord } from "../api/attendanceApi";
import AttendanceActionModal, { AttendanceAction } from "./AttendanceActionModal";
import Tooltip from "shared/Tooltip";
import { LEAVE_TYPE_LABELS } from "../api/leaveApi";

interface Props {
  day: AttendanceDaySummary;
  writeMode: "admin-direct" | "hr-request" | "readonly";
  onClose: () => void;
  onSuccess: () => void;
}

type NestedModalState = { action: AttendanceAction; punch?: AttendancePunch; defaultStatus?: number };

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

export default function AttendanceDayDetailModal({ day: initialDay, writeMode, onClose, onSuccess }: Props) {
  const [day, setDay] = useState(initialDay);
  const [nested, setNested] = useState<NestedModalState | null>(null);
  const canWrite = writeMode !== "readonly";

  const refresh = async () => {
    try {
      const fresh = await fetchAttendanceDayDetail(day.employee_code, day.date);
      setDay(fresh);
    } catch {
      // Last punch for the day was deleted — nothing left to show here.
      onSuccess();
      onClose();
      return;
    }
    onSuccess();
  };

  const missingSides: { status: number; label: string }[] = [];
  if (!day.check_in) missingSides.push({ status: 0, label: "Check-In" });
  if (!day.check_out) missingSides.push({ status: 1, label: "Check-Out" });

  return (
    <>
      <Modal size="md" onClick={onClose} closeButtonCLick={onClose} modalClassName="p-6">
        <h2 className="text-lg font-semibold text-foreground font-inter">{day.employee_name}</h2>
        <p className="text-sm text-muted-foreground font-inter mb-4 flex items-center gap-1.5">
          {day.employee_code} · {formatDate(day.date)}
          {day.is_late && (
            <Tooltip
              content={`First arrival was ${day.late_by_minutes} minutes after the 10:00 AM PKT shift start (30-minute grace period)`}
              position="bottom"
            >
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium font-inter bg-amber-500/15 text-amber-500">
                First arrival: Late {day.late_by_minutes}m
              </span>
            </Tooltip>
          )}
          {day.is_leave && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium font-inter bg-violet-500/15 text-violet-500">
              {day.leave_type ? `On Leave: ${LEAVE_TYPE_LABELS[day.leave_type] || day.leave_type}` : "On Leave"}
            </span>
          )}
        </p>

        {day.all_punches.length === 0 && (
          <p className={`text-sm font-inter mb-3 ${day.is_leave ? "text-violet-500" : "text-destructive"}`}>
            {day.is_leave ? "No punches — approved leave." : "No punches recorded for this day."}
          </p>
        )}

        <div className="space-y-2">
          {day.all_punches.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 bg-surface-muted border border-border rounded-xl"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-inter shrink-0 ${
                    p.status === 0
                      ? "bg-emerald-500/10 text-emerald-400"
                      : p.status === 1
                      ? "bg-red-500/10 text-red-400"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {p.status_label}
                </span>
                <span className="text-sm text-foreground font-mono font-inter">{formatTime(p.timestamp)}</span>
                <span className="text-xs text-muted-foreground font-mono font-inter truncate">{p.serial_number}</span>
                {p.is_manually_modified && (
                  <Tooltip asLabel content={`${p.modified_action === "add" ? "Added" : p.modified_action === "delete" ? "Removed" : "Edited"} by ${p.modified_by}${p.modified_at ? ` on ${new Date(p.modified_at).toLocaleString()}` : ""}`}>
                    <History className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  </Tooltip>
                )}
              </div>
              {canWrite && (
                <div className="flex items-center gap-1 shrink-0">
                  <Tooltip asLabel content={writeMode === "admin-direct" ? "Edit" : "Request correction"}>
                    <button
                      onClick={() => setNested({ action: "edit", punch: p })}
                      className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface rounded-lg transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                  <Tooltip asLabel content={writeMode === "admin-direct" ? "Delete" : "Request removal"} align="right">
                    <button
                      onClick={() => setNested({ action: "delete", punch: p })}
                      className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
          ))}
        </div>

        {canWrite && missingSides.length > 0 && (
          <div className="flex gap-2 mt-4">
            {missingSides.map((side) => (
              <Button
                key={side.status}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNested({ action: "add", defaultStatus: side.status })}
              >
                <Plus className="w-3.5 h-3.5" /> Add {side.label}
              </Button>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>

      {nested && (
        <AttendanceActionModal
          writeMode={writeMode === "admin-direct" ? "admin-direct" : "hr-request"}
          action={nested.action}
          record={nested.punch ? toAttendanceRecord(day, nested.punch) : undefined}
          defaultEmployeeCode={day.employee_code}
          defaultStatus={nested.defaultStatus}
          defaultDate={day.date}
          onClose={() => setNested(null)}
          onSuccess={refresh}
        />
      )}
    </>
  );
}
